import $ from 'jquery';

/**
 * Query History and Favorites Management
 * Provides UI for displaying and managing query history and favorites
 */

class QueryHistoryManager {
  constructor() {
    this.historyCache = [];
    this.favoritesCache = [];
    this.currentQuery = '';
    this.apiBase =
      window.location.origin.includes('localhost') || window.location.origin.includes('helix.local')
        ? 'http://helix.local:6060'
        : window.location.origin;

    this.init();
  }

  init() {
    // Wait for DOM ready
    $(() => {
      this.setupUI();
      this.attachEventHandlers();
      this.loadHistory();
      this.loadFavorites();
    });
  }

  setupUI() {
    // Add history dropdown to query input if it doesn't exist
    if (!$('#query-history-dropdown').length) {
      const historyHTML = `
        <div id="query-history-dropdown" class="query-history-dropdown" style="display: none;">
          <div class="history-tabs">
            <button class="tab-btn active" data-tab="favorites">★ Favorites</button>
            <button class="tab-btn" data-tab="recent">Recent</button>
          </div>
          <div class="history-content">
            <div id="favorites-list" class="history-list active"></div>
            <div id="recent-list" class="history-list"></div>
          </div>
        </div>
      `;

      // Find query input and add dropdown
      const $queryInput = $('#query-input, .query-input, input[name="query"]').first();
      if ($queryInput.length) {
        $queryInput.parent().css('position', 'relative');
        $queryInput.after(historyHTML);
      }
    }

    // Add styles if not already present
    if (!$('#query-history-styles').length) {
      const styles = `
        <style id="query-history-styles">
          .query-history-dropdown {
            position: absolute;
            left: 0;
            right: 0;
            background: white;
            border: 1px solid #ddd;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            z-index: 1000;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
          }

          .query-history-dropdown--down {
            top: calc(100% + 8px);
            bottom: auto;
          }

          .query-history-dropdown--up {
            top: auto;
            bottom: calc(100% + 8px);
          }
          
          .history-tabs {
            display: flex;
            border-bottom: 1px solid #eee;
            position: sticky;
            top: 0;
            background: #fff;
            z-index: 1;
          }
          
          .tab-btn {
            flex: 1;
            padding: 10px;
            background: none;
            border: none;
            cursor: pointer;
            font-size: 14px;
            color: #666;
            transition: all 0.2s;
          }
          
          .tab-btn.active {
            color: #1976d2;
            border-bottom: 2px solid #1976d2;
          }
          
          .tab-btn:hover {
            background: #f5f5f5;
          }
          
          .history-list {
            display: none;
            padding: 8px 0;
          }
          
          .history-list.active {
            display: block;
          }

          .history-content {
            display: block;
          }
          
          .history-item {
            padding: 10px 15px;
            cursor: pointer;
            transition: background 0.2s;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          
          .history-item:hover {
            background: #f5f5f5;
          }
          
          .history-item-text {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 14px;
          }
          
          .history-item-meta {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: #999;
          }
          
          .star-btn {
            background: none;
            border: none;
            cursor: pointer;
            font-size: 18px;
            color: #ccc;
            transition: color 0.2s;
            padding: 0;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .star-btn.starred {
            color: #ffd700;
          }
          
          .star-btn:hover {
            color: #ffd700;
          }
          
          .query-badge {
            background: #e3f2fd;
            color: #1976d2;
            padding: 2px 6px;
            border-radius: 3px;
            font-size: 11px;
            font-weight: 500;
          }
          
          .empty-state {
            padding: 20px;
            text-align: center;
            color: #999;
            font-size: 14px;
          }
        </style>
      `;
      $('head').append(styles);
    }
  }

  positionDropdown() {
    const $dropdown = $('#query-history-dropdown');
    const $queryInput = $('#query-input, .query-input, input[name="query"]').first();

    if (!$dropdown.length || !$queryInput.length) {
      return;
    }

    const rect = $queryInput.get(0).getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceAbove = Math.max(0, rect.top);
    const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
    const openUp = spaceBelow < 320 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(180, Math.min(400, (openUp ? spaceAbove : spaceBelow) - 16));

    $dropdown
      .toggleClass('query-history-dropdown--up', openUp)
      .toggleClass('query-history-dropdown--down', !openUp)
      .css('max-height', `${maxHeight}px`);
  }

  attachEventHandlers() {
    // Tab switching
    $(document).on('click', '.tab-btn', e => {
      const tab = $(e.target).data('tab');
      $('.tab-btn').removeClass('active');
      $(e.target).addClass('active');
      $('.history-list').removeClass('active');
      $(`#${tab}-list`).addClass('active');
    });

    // Pin to dashboard
    $(document).on('click', '.history-pin-btn', e => {
      e.stopPropagation();
      const query = $(e.currentTarget).data('query');
      if (!query) return;

      if (typeof window.fetchAndRenderGraphForQuery !== 'function') {
        if (window.snacks) window.snacks.showSnackbar({ message: 'App not ready yet, try again', timeout: 2500 });
        return;
      }

      const graphId = Math.random().toString(36).substring(7);

      window.fetchAndRenderGraphForQuery(query, [], graphId)
        .then(function() {
          if (window.dataAccessor && window.userData) {
            window.dataAccessor.addGraphToCurrentDashboard(window.userData, graphId, query);
          }
          if (window.snacks) window.snacks.showSnackbar({ message: 'Pinned to dashboard', timeout: 2500 });
        })
        .catch(function(err) {
          console.error('Pin to dashboard failed:', err);
          if (window.snacks) window.snacks.showSnackbar({ message: 'Failed to pin query', timeout: 3000 });
        });
    });

    // Query selection
    $(document).on('click', '.history-item', e => {
      if (!$(e.target).closest('.star-btn, .history-pin-btn').length) {
        const query = $(e.currentTarget).data('query');
        this.selectQuery(query);
      }
    });

    // Star/unstar
    $(document).on('click', '.star-btn', async e => {
      e.stopPropagation();
      const $btn = $(e.currentTarget);
      const queryId = $btn.data('query-id');
      await this.toggleFavorite(queryId);
    });

    // Show/hide dropdown on input focus
    const $queryInput = $('#query-input, .query-input, input[name="query"]').first();

    $queryInput.on('focus', () => {
      this.showDropdown();
    });

    $queryInput.on('input', e => {
      const value = $(e.target).val();
      this.filterHistory(value);
    });

    // Hide dropdown when clicking outside
    $(document).on('click', e => {
      if (!$(e.target).closest('.query-history-dropdown, #query-input, .query-input').length) {
        this.hideDropdown();
      }
    });

    $(window).on('resize.queryHistory scroll.queryHistory', () => {
      if ($('#query-history-dropdown').is(':visible')) {
        this.positionDropdown();
      }
    });
  }

  async loadHistory() {
    try {
      const secret = window.localStorage.getItem('userSecret') || window.userSecret;
      if (!secret) return;

      const response = await fetch(`${this.apiBase}/api/query-history/list?limit=20`, {
        headers: {
          Authorization: `Bearer ${secret}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.historyCache = data.history || [];
        this.renderHistory();
      }
    } catch (error) {
      console.error('Failed to load query history:', error);
    }
  }

  async loadFavorites() {
    try {
      const secret = window.localStorage.getItem('userSecret') || window.userSecret;
      if (!secret) return;

      const response = await fetch(`${this.apiBase}/api/query-history/favorites`, {
        headers: {
          Authorization: `Bearer ${secret}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        this.favoritesCache = data.favorites || [];
        this.renderFavorites();
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  }

  renderHistory() {
    const $list = $('#recent-list');

    if (this.historyCache.length === 0) {
      $list.html('<div class="empty-state">No recent queries</div>');
      return;
    }

    const html = this.historyCache
      .map(
        item => `
      <div class="history-item" data-query="${this.escapeHtml(item.query_text)}" data-id="${item.id}">
        <div class="history-item-text">${this.escapeHtml(item.query_text)}</div>
        <div class="history-item-meta">
          ${
  item.tags && item.tags.length
    ? item.tags.map(tag => `<span class="query-badge">${tag}</span>`).join('')
    : ''
}
          <span>${this.formatTime(item.last_accessed)}</span>
          <button class="history-pin-btn" data-query="${this.escapeHtml(item.query_text)}" title="Pin to current dashboard" type="button">
            <i class="material-icons" style="font-size:15px;vertical-align:middle">push_pin</i>
          </button>
          <button class="star-btn ${item.is_favorite ? 'starred' : ''}"
                  data-query-id="${item.id}"
                  title="${item.is_favorite ? 'Remove from favorites' : 'Add to favorites'}">
            ★
          </button>
        </div>
      </div>
    `
      )
      .join('');

    $list.html(html);
  }

  renderFavorites() {
    const $list = $('#favorites-list');

    if (this.favoritesCache.length === 0) {
      $list.html(
        '<div class="empty-state">No favorite queries yet.<br>Star your commonly used queries for quick access.</div>'
      );
      return;
    }

    const html = this.favoritesCache
      .map(
        item => `
      <div class="history-item" data-query="${this.escapeHtml(item.query_text)}" data-id="${item.id}">
        <div class="history-item-text">${this.escapeHtml(item.query_text)}</div>
        <div class="history-item-meta">
          ${
  item.tags && item.tags.length
    ? item.tags.map(tag => `<span class="query-badge">${tag}</span>`).join('')
    : ''
}
          <span>${item.access_count || 1}x used</span>
          <button class="history-pin-btn" data-query="${this.escapeHtml(item.query_text)}" title="Pin to current dashboard" type="button">
            <i class="material-icons" style="font-size:15px;vertical-align:middle">push_pin</i>
          </button>
          <button class="star-btn starred"
                  data-query-id="${item.id}"
                  title="Remove from favorites">
            ★
          </button>
        </div>
      </div>
    `
      )
      .join('');

    $list.html(html);
  }

  async toggleFavorite(queryId) {
    try {
      const secret = window.localStorage.getItem('userSecret') || window.userSecret;
      if (!secret) return;

      const response = await fetch(`${this.apiBase}/api/query-history/toggle-favorite`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${secret}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query_id: queryId })
      });

      if (response.ok) {
        const data = await response.json();

        // Update cache and UI
        const historyItem = this.historyCache.find(item => item.id === queryId);
        if (historyItem) {
          historyItem.is_favorite = data.is_favorite;
        }

        // Reload both lists
        await Promise.all([this.loadHistory(), this.loadFavorites()]);
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  }

  selectQuery(query) {
    const $input = $('#query-input, .query-input, input[name="query"]').first();
    $input.val(query);
    this.hideDropdown();

    // Trigger query execution if there's a submit button
    const $submitBtn = $input
      .closest('form')
      .find('button[type="submit"], .query-submit-btn')
      .first();
    if ($submitBtn.length) {
      $submitBtn.click();
    }
  }

  filterHistory(searchText) {
    if (!searchText || searchText.length < 2) {
      this.renderHistory();
      this.renderFavorites();
      return;
    }

    const searchLower = searchText.toLowerCase();

    // Filter history
    const filteredHistory = this.historyCache.filter(item =>
      item.query_text.toLowerCase().includes(searchLower)
    );

    // Filter favorites
    const filteredFavorites = this.favoritesCache.filter(item =>
      item.query_text.toLowerCase().includes(searchLower)
    );

    // Re-render with filtered results
    this.renderFilteredResults(filteredHistory, filteredFavorites);
  }

  renderFilteredResults(history, favorites) {
    // Render filtered favorites
    const $favoritesList = $('#favorites-list');
    if (favorites.length === 0) {
      $favoritesList.html('<div class="empty-state">No matching favorites</div>');
    } else {
      const favoritesHtml = favorites
        .map(
          item => `
        <div class="history-item" data-query="${this.escapeHtml(item.query_text)}" data-id="${item.id}">
          <div class="history-item-text">${this.highlightMatch(item.query_text, this.currentQuery)}</div>
          <div class="history-item-meta">
            <button class="history-pin-btn" data-query="${this.escapeHtml(item.query_text)}" title="Pin to current dashboard" type="button"><i class="material-icons" style="font-size:15px;vertical-align:middle">push_pin</i></button>
            <button class="star-btn starred" data-query-id="${item.id}">★</button>
          </div>
        </div>
      `
        )
        .join('');
      $favoritesList.html(favoritesHtml);
    }

    // Render filtered history
    const $historyList = $('#recent-list');
    if (history.length === 0) {
      $historyList.html('<div class="empty-state">No matching queries</div>');
    } else {
      const historyHtml = history
        .map(
          item => `
        <div class="history-item" data-query="${this.escapeHtml(item.query_text)}" data-id="${item.id}">
          <div class="history-item-text">${this.highlightMatch(item.query_text, this.currentQuery)}</div>
          <div class="history-item-meta">
            <button class="history-pin-btn" data-query="${this.escapeHtml(item.query_text)}" title="Pin to current dashboard" type="button"><i class="material-icons" style="font-size:15px;vertical-align:middle">push_pin</i></button>
            <button class="star-btn ${item.is_favorite ? 'starred' : ''}" data-query-id="${item.id}">★</button>
          </div>
        </div>
      `
        )
        .join('');
      $historyList.html(historyHtml);
    }
  }

  highlightMatch(text, search) {
    if (!search) return this.escapeHtml(text);

    const escaped = this.escapeHtml(text);
    const searchEscaped = this.escapeHtml(search);
    const regex = new RegExp(`(${searchEscaped})`, 'gi');

    return escaped.replace(regex, '<strong>$1</strong>');
  }

  showDropdown() {
    this.positionDropdown();
    $('#query-history-dropdown').stop(true, true).fadeIn(200);
  }

  hideDropdown() {
    $('#query-history-dropdown').stop(true, true).fadeOut(200);
  }

  formatTime(timestamp) {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  // Public method to save query after execution
  async saveQuery(queryText, queryType = 'graph', resultSummary = null, tags = []) {
    try {
      const secret = window.localStorage.getItem('userSecret') || window.userSecret;
      if (!secret) return;

      const response = await fetch(`${this.apiBase}/api/query-history/save`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${secret}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query_text: queryText,
          query_type: queryType,
          result_summary: resultSummary,
          tags: tags,
          success: true
        })
      });

      if (response.ok) {
        // Reload history to show the new query
        await this.loadHistory();
      }
    } catch (error) {
      console.error('Failed to save query:', error);
    }
  }
}

// Initialize and export
const queryHistoryManager = new QueryHistoryManager();

// Attach to window for global access
if (typeof window !== 'undefined') {
  window.QueryHistoryManager = queryHistoryManager;
  window.queryHistoryManager = queryHistoryManager;
}

export default queryHistoryManager;
